/**
 * Ficha de leitura da operação montada.
 *
 * A informação é de três naturezas diferentes — o enredo (narrativa), os dados
 * da cadeia (rota e ICMS) e o elenco (nós por papel) — e antes as três vinham
 * na mesma grade de campos, todas com o mesmo rótulo em caixa alta. Aqui cada
 * uma ganha um bloco próprio, separado por régua, para o olho saber onde está.
 */

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Resumo } from "@/types";

/** A rota chega como texto único ("A → B → C"); aqui vira uma cadeia legível. */
function Cadeia({ rota }: { rota: string }) {
  const etapas = rota
    .split(/→|->/)
    .map((etapa) => etapa.trim())
    .filter(Boolean);

  if (!etapas.length) return <span className="text-muted-foreground">—</span>;

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
      {etapas.map((etapa, i) => (
        <span key={`${etapa}-${i}`} className="flex items-center gap-1.5">
          {i > 0 && <span aria-hidden className="text-sm text-primary/50">→</span>}
          <span className="rounded-md bg-secondary px-2 py-1 text-xs font-bold text-fs-escuro">
            {etapa}
          </span>
        </span>
      ))}
    </div>
  );
}

export function ResumoOperacao({ resumo }: { resumo: Resumo }) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="uppercase tracking-wide">Resumo da operação</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{resumo.natureza}</Badge>
            <Badge variant="neutro">
              <span className="numerico">{resumo.totalNfs}</span>
              &nbsp;{resumo.totalNfs === 1 ? "nota" : "notas"}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {resumo.narrativa && (
          <p className="rounded-lg border-l-[3px] border-primary/60 bg-accent px-4 py-3 text-sm leading-relaxed text-accent-foreground">
            {resumo.narrativa}
          </p>
        )}

        <div className="grid gap-4 border-t border-border/70 pt-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-6">
          <div>
            <div className="rotulo-seco mb-2">Cadeia</div>
            <Cadeia rota={resumo.rota} />
          </div>
          <div>
            <div className="rotulo-seco mb-2">Tratamento de ICMS</div>
            <p className="text-sm leading-relaxed">{resumo.tratamentos || "—"}</p>
          </div>
        </div>

        {resumo.porPapel.length > 0 && (
          <div className="border-t border-border/70 pt-4">
            <div className="rotulo-seco mb-2.5">Participantes</div>
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {resumo.porPapel.map((papel) => (
                <div
                  key={papel.papel}
                  className="rounded-lg border border-border/70 bg-secondary/40 px-3 py-2.5"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-extrabold uppercase tracking-[0.06em] text-fs-escuro">
                      {papel.papel}
                    </span>
                    <span className="numerico text-[11px] text-muted-foreground">
                      {papel.nos.length}
                    </span>
                  </div>
                  <ul className="mt-1.5 space-y-0.5">
                    {papel.nos.map((no) => (
                      <li key={no} className="text-xs leading-relaxed text-muted-foreground">
                        {no}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
